import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Plus, MessageSquare, Loader2, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useDeleteSession, useRenameSession } from './use-chat'
import type { ChatSession } from './chat-api'

interface SessionListProps {
  sessions: ChatSession[]
  activeId: string | null
  isLoading: boolean
  isCreating: boolean
  inSheet?: boolean
  onConfirmationOpenChange?: (open: boolean) => void
  onSelect: (id: string) => void
  onCreate: () => void
}

const dateFormatter = new Intl.DateTimeFormat('de-CH', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

export function SessionList({
  sessions,
  activeId,
  isLoading,
  isCreating,
  inSheet = false,
  onConfirmationOpenChange,
  onSelect,
  onCreate,
}: SessionListProps) {
  const rename = useRenameSession()
  const del = useDeleteSession()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const deleteOpenerRef = useRef<HTMLButtonElement | null>(null)
  const createButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingId])

  const commit = (sessionId: string) => {
    const value = draft.trim()
    if (!value) {
      setEditingId(null)
      return
    }
    const current = sessions.find((s) => s.id === sessionId)
    if (current && current.title === value) {
      setEditingId(null)
      return
    }
    rename.mutate(
      { sessionId, title: value },
      { onSettled: () => setEditingId(null) },
    )
  }

  const handleKey = (e: KeyboardEvent<HTMLInputElement>, sessionId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit(sessionId)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditingId(null)
    }
  }

  return (
    <aside className="flex h-full w-full flex-col border-r border-border bg-muted/30">
      <div className={cn('border-b border-border p-3', inSheet && 'pr-16')}>
        <Button
          type="button"
          ref={createButtonRef}
          onClick={onCreate}
          disabled={isCreating}
          className="h-11 w-full justify-start min-[761px]:h-9"
          size="sm"
        >
          {isCreating ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Neuer Chat
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}

        {!isLoading && sessions.length === 0 && (
          <div className="px-4 py-8 text-center text-small text-muted-foreground">
            <MessageSquare className="mx-auto mb-2 h-6 w-6 opacity-50" />
            Noch keine Chats.
          </div>
        )}

        <ul className="flex flex-col">
          {sessions.map((s) => {
            const active = s.id === activeId
            const isEditing = editingId === s.id
            const display = s.title?.trim() ? s.title : 'Neuer Chat'
            return (
              <li key={s.id}>
                <div
                  className={cn(
                    'group relative flex w-full flex-col items-start gap-0.5 border-l-2 px-3 py-2.5 text-left transition-colors',
                    active
                      ? 'border-l-primary bg-primary/5'
                      : 'border-l-transparent hover:bg-muted/60',
                  )}
                >
                  {isEditing ? (
                    <input
                      ref={inputRef}
                      aria-label="Chat umbenennen"
                      type="text"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={() => commit(s.id)}
                      onKeyDown={(e) => handleKey(e, s.id)}
                      maxLength={200}
                      disabled={rename.isPending}
                      className="min-h-11 w-full rounded border border-border bg-background px-1.5 py-1 text-[16px] focus:outline-none focus:ring-2 focus:ring-ring min-[761px]:min-h-9 min-[761px]:text-sm"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSelect(s.id)}
                      onDoubleClick={(e) => {
                        e.stopPropagation()
                        setDraft(s.title ?? '')
                        setEditingId(s.id)
                      }}
                      title="Doppelklick zum Umbenennen"
                      className="min-h-11 w-full pr-24 text-left min-[761px]:min-h-9 min-[761px]:pr-20"
                    >
                      <span
                        className={cn(
                          'block truncate text-sm',
                          active ? 'font-medium text-foreground' : 'text-foreground/80',
                        )}
                        title={display}
                      >
                        {display}
                      </span>
                    </button>
                  )}
                  <span className="text-[12px] text-muted-foreground">
                    {dateFormatter.format(new Date(s.updated_at))}
                  </span>
                  {!isEditing && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDraft(s.title ?? '')
                          setEditingId(s.id)
                        }}
                        title="Chat umbenennen"
                        aria-label="Chat umbenennen"
                        className="absolute right-[52px] min-[761px]:right-11 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:h-9 min-[761px]:w-9 min-[761px]:opacity-0 min-[761px]:group-hover:opacity-100 min-[761px]:focus:opacity-100"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteOpenerRef.current = e.currentTarget
                          onConfirmationOpenChange?.(true)
                          setConfirmDeleteId(s.id)
                        }}
                        title="Chat löschen"
                        aria-label="Chat löschen"
                        className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-[761px]:h-9 min-[761px]:w-9 min-[761px]:opacity-0 min-[761px]:group-hover:opacity-100 min-[761px]:focus:opacity-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      </div>

      <Dialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => {
          if (!open && !del.isPending) setConfirmDeleteId(null)
        }}
      >
        <DialogContent
          closeDisabled={del.isPending}
          onEscapeKeyDown={(event) => {
            if (del.isPending) event.preventDefault()
          }}
          onKeyDownCapture={(event) => {
            if (event.key !== 'Escape') return
            // Radix layer listeners register in effects. Handle a fast Escape
            // in the focused confirmation even before that registration settles.
            event.preventDefault()
            event.stopPropagation()
            if (!del.isPending) setConfirmDeleteId(null)
          }}
          onPointerDownOutside={(event) => {
            if (del.isPending) event.preventDefault()
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            const opener = deleteOpenerRef.current
            if (opener?.isConnected && opener.getClientRects().length > 0) opener.focus()
            else createButtonRef.current?.focus()
            // Keep the containing sheet open through the native close event.
            onConfirmationOpenChange?.(false)
          }}
        >
          <DialogHeader>
            <DialogTitle>Chat löschen?</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Diesen Chat wirklich löschen? Inhalte gehen verloren.
          </DialogDescription>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmDeleteId(null)}
              disabled={del.isPending}
            >
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              disabled={del.isPending}
              onClick={() => {
                if (!confirmDeleteId) return
                const id = confirmDeleteId
                del.mutate(id, {
                  onSettled: () => setConfirmDeleteId(null),
                })
              }}
            >
              {del.isPending ? 'Lösche…' : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  )
}
